import {AfterViewInit, Component, ElementRef, Input, NgZone, OnDestroy, OnInit} from '@angular/core';

@Component({
  standalone: false,
  selector: 'esp-multi-select-item',
  templateUrl: './multi-select-item.component.html',
  styleUrls: ['./multi-select-item.component.css']
})
export class MultiSelectItemComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() item;

  // Watches the PrimeNG option row so the de-duplicated a11y semantics survive
  // PrimeNG re-applying its host bindings on every change-detection pass.
  private optionObserver: MutationObserver | null = null;

  constructor(private el: ElementRef, private zone: NgZone) { }

  ngOnInit(): void {
  }

  /**
   * a11y (WCAG 4.1.2 Name, Role, Value / 3.3.2 / 2.1.1 Keyboard): PrimeNG 21
   * renders every multiselect option as `<li role="option" aria-label="X"
   * aria-selected aria-checked>` AND embeds its own `<p-checkbox
   * [ariaLabel]="X">` inside the row. A screen reader therefore announces the
   * label and state several times ("not selected X, not checked X, checkbox not
   * checked, to check press spacebar").
   *
   * Collapse it to ONE concise option announcement ("X, selected" / "X, not
   * selected"):
   *  - drop the row's `aria-checked`. `aria-checked` is NOT a valid state for
   *    `role="option"` (the ARIA listbox pattern conveys selection via
   *    `aria-selected`); keeping the invalid `aria-checked` both duplicates the
   *    state announcement AND makes JAWS treat the row as a stray checkbox
   *    rather than a listbox option, so arrow-key navigation walks out of the
   *    list and collapses the combobox. Keeping only `aria-selected` restores
   *    standard multi-select listbox navigation and announces the state once.
   *    This matches the app's single-select helper
   *    (`enforceMultiselectSingleSelectAria`), which likewise keeps
   *    `aria-selected`.
   *  - hide PrimeNG's inner `p-checkbox` (a visual affordance) from the a11y
   *    tree so it does not add a second name + state + instruction.
   * The visible label and our own decorative checkbox are already presentational
   * (aria-hidden), so the row's single `aria-label` names it once.
   */
  ngAfterViewInit(): void {
    const optionRow: HTMLElement | null =
      this.el.nativeElement?.closest('li[role="option"]') ??
      this.el.nativeElement?.closest('li');
    if (!optionRow || typeof MutationObserver === 'undefined') {
      return;
    }

    const dedupe = (): void => {
      if (optionRow.hasAttribute('aria-checked')) {
        optionRow.removeAttribute('aria-checked');
      }
      const primeCheckbox = optionRow.querySelector('p-checkbox');
      if (primeCheckbox && primeCheckbox.getAttribute('aria-hidden') !== 'true') {
        primeCheckbox.setAttribute('aria-hidden', 'true');
      }
    };

    this.zone.runOutsideAngular(() => {
      dedupe();
      this.optionObserver = new MutationObserver(dedupe);
      this.optionObserver.observe(optionRow, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-selected', 'aria-checked'],
      });
    });
  }

  ngOnDestroy(): void {
    this.optionObserver?.disconnect();
    this.optionObserver = null;
  }

}
